/**
 * Shared pieces of the Vello chart components (D507): the motion state, the measured width, the old → new tween of a
 * list of numbers, the active-point (hover + keyboard) state, the tooltip and the loading / empty states.
 *
 * Motion model (docs/ai/19 "Regras de gráfico e de movimento", amended by doc 21 / D549 "reduced motion like the design"):
 *   - ENTRANCE happens once, on mount, in CSS (`charts.css`): bars grow from the baseline, a line draws, a ring sweeps.
 *     The chart root carries `data-motion` — `on` (entrance runs) or `off` (no motion: a test, a synthetic DOM, or
 *     `animate={false}`). The design stills ONLY its `.fx-*` effect classes under `prefers-reduced-motion`; its bar entrances and
 *     its count-up keep running, and so do ours. (The live pulse and the progress sweep ARE `.fx-*` in the design and are stilled
 *     in `charts.css`.) `usePrefersReducedMotion` stays exported for callers that want their own policy.
 *   - A DATA CHANGE tweens the drawn numbers from the old values to the new ones in JS (`useTweenedValues`) with the
 *     standard easing — no re-entrance, no restart. `off` jumps to the new values.
 */
import { useCallback, useEffect, useLayoutEffect, useRef, useState, useSyncExternalStore, type CSSProperties, type KeyboardEvent, type PointerEvent, type ReactElement, type ReactNode, type RefObject } from 'react';
import { CHART_MOTION_MS, CHART_STAGGER_MAX_STEPS, EFFECT_CLASS } from '@vello/tokens';
import { easeStandard, isFiniteNumber, lerpValues, roundCoord } from '@vello/charts-core';

// ── Series colour roles ────────────────────────────────────────────────────────────────────────────────────────────

/** A chart colour intent: `neutral` is the ink series; the others mark a STATE with the accessible semantic text roles. */
export type ChartToneName = 'neutral' | 'success' | 'warning' | 'danger';

/** The CSS colour of a tone (a `--chart-*` variable; the semantic tones alias the accessible `*-text` roles). */
export function toneColor(tone: ChartToneName): string {
  return tone === 'neutral' ? 'var(--chart-series-1)' : `var(--chart-tone-${tone})`;
}

/** The colour of the `index`-th series (zero-based): the ordered quiet scale, cycling after four. */
export function seriesColor(index: number): string {
  return `var(--chart-series-${(Math.max(0, Math.floor(index)) % 4) + 1})`;
}

/**
 * The stroke dash pattern of the `index`-th line series. Colour is never the only signal: neighbouring series also differ
 * by dash (solid, dashed, dotted, dash-dot). Pattern lengths are SVG user units.
 */
export function seriesDash(index: number): string | undefined {
  const patterns = [undefined, '6 4', '1 4', '10 3 2 3'] as const;
  return patterns[Math.max(0, Math.floor(index)) % patterns.length];
}

/** A series colour, unless a tone overrides it. */
export function colorOf(index: number, tone?: ChartToneName): string {
  return tone !== undefined && tone !== 'neutral' ? toneColor(tone) : seriesColor(index);
}

/** The default value formatter when the caller injects none: integers as-is, otherwise at most two decimals. */
export function defaultFormatValue(value: number): string {
  return String(Number.isInteger(value) ? value : roundCoord(value, 2));
}

// ── Motion ─────────────────────────────────────────────────────────────────────────────────────────────────────────

export type MotionState = 'on' | 'off';

const REDUCE_QUERY = '(prefers-reduced-motion: reduce)';

function subscribeReduced(onChange: () => void): () => void {
  if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return () => undefined;
  const mq = window.matchMedia(REDUCE_QUERY);
  mq.addEventListener?.('change', onChange);
  return () => mq.removeEventListener?.('change', onChange);
}

function readReduced(): boolean {
  return typeof window !== 'undefined' && typeof window.matchMedia === 'function' && window.matchMedia(REDUCE_QUERY).matches;
}

/** A DOM without a rendering engine (jsdom, happy-dom): there is nothing to animate and a test must see final values. */
function isSyntheticDom(): boolean {
  return typeof navigator !== 'undefined' && /jsdom|happy-?dom/i.test(navigator.userAgent);
}

/** Whether the user asked for reduced motion (live: follows the setting while the page is open). */
export function usePrefersReducedMotion(): boolean {
  return useSyncExternalStore(subscribeReduced, readReduced, () => false);
}

/**
 * The motion state of a chart. `animate={false}` forces `off`; `animate={true}` forces motion on. Left undefined it is `on` in
 * a real browser and `off` in a synthetic DOM, so rendered tests see final values on the first render without a timer.
 * `prefers-reduced-motion` does NOT switch chart motion off (the design keeps its non-`.fx-*` motion under that setting).
 */
export function useMotionState(animate?: boolean): MotionState {
  if (animate === false) return 'off';
  if (animate === true) return 'on';
  return isSyntheticDom() ? 'off' : 'on';
}

function sameValues(a: readonly (number | null)[], b: readonly (number | null)[]): boolean {
  return a.length === b.length && a.every((v, i) => v === b[i]);
}

/**
 * Tween the drawn numbers old → new when `target` changes (once mounted). The first render returns `target` as it is (the
 * entrance is CSS). With `motion !== 'on'` the new values are returned at once. The result always has the length of `target`.
 */
export function useTweenedValues(target: readonly (number | null)[], motion: MotionState, durationMs: number = CHART_MOTION_MS.tween): readonly (number | null)[] {
  const key = target.map((v) => (v === null ? 'n' : String(v))).join('|');
  const [shown, setShown] = useState<readonly (number | null)[]>(target);
  const shownRef = useRef<readonly (number | null)[]>(target);
  const targetRef = useRef(target);
  targetRef.current = target;

  useEffect(() => {
    const to = targetRef.current;
    if (motion !== 'on' || typeof requestAnimationFrame !== 'function') {
      shownRef.current = to;
      setShown(to);
      return undefined;
    }
    const from = shownRef.current;
    if (sameValues(from, to)) return undefined;
    const startedAt = performance.now();
    let frame = 0;
    const step = (now: number): void => {
      const t = Math.min(1, (now - startedAt) / durationMs);
      const next = t >= 1 ? to : lerpValues(from, to, easeStandard(t));
      shownRef.current = next;
      setShown(next);
      if (t < 1) frame = requestAnimationFrame(step);
    };
    frame = requestAnimationFrame(step);
    return () => cancelAnimationFrame(frame);
  }, [key, motion, durationMs]);

  if (motion !== 'on') return target;
  return shown.length === target.length ? shown : target;
}

/** The inline style of an entrance item: its place in the cascade (CSS multiplies it by the stagger token). */
export function cascade(index: number): CSSProperties {
  return { '--i': Math.min(Math.max(0, Math.floor(index)), CASCADE_MAX_STEPS) } as CSSProperties;
}

/** The longest cascade, in steps, used by {@link cascade} (FX-07: items past the sixth enter together with it). */
export const CASCADE_MAX_STEPS = CHART_STAGGER_MAX_STEPS;

// ── Size ───────────────────────────────────────────────────────────────────────────────────────────────────────────

/**
 * The drawable width of an element. The first measure runs before paint (no flash at the wrong size); a ResizeObserver
 * follows later changes. Where nothing can be measured (no layout engine) the fallback is used.
 */
export function useElementWidth(ref: RefObject<HTMLElement | null>, fallback: number): number {
  const [width, setWidth] = useState(fallback);
  useLayoutEffect(() => {
    const el = ref.current;
    if (el === null) return undefined;
    const read = (): void => {
      const w = el.getBoundingClientRect().width;
      if (isFiniteNumber(w) && w > 0) setWidth((prev) => (Math.abs(prev - w) < 0.5 ? prev : w));
    };
    read();
    if (typeof ResizeObserver === 'undefined') return undefined;
    const observer = new ResizeObserver(read);
    observer.observe(el);
    return () => observer.disconnect();
  }, [ref]);
  return width;
}

// ── Active point (hover + keyboard) ────────────────────────────────────────────────────────────────────────────────

export interface ActivePoint {
  readonly index: number | null;
  readonly set: (index: number | null) => void;
  readonly onKeyDown: (event: KeyboardEvent<HTMLElement>) => void;
  readonly onFocus: () => void;
  readonly onBlur: () => void;
  readonly onPointerLeave: () => void;
  /** Call from the plot's pointer handler with the index under the pointer. */
  readonly fromPointer: (event: PointerEvent<Element>, indexAt: (x: number) => number) => void;
  /** Call from the plot's click handler: reports the index under the pointer to `onActivate` (D529: the selection contract). */
  readonly activateFromPointer: (event: { clientX: number; currentTarget: Element }, indexAt: (x: number) => number) => void;
}

/**
 * Which point of a chart is "active" (shown in the tooltip). Pointer hover sets it from the position; the keyboard moves it
 * (Left/Right/Up/Down by one, Home/End to the ends, Escape to clear). On keyboard focus the chart opens on its LAST point,
 * the most recent one. A chart with no points never has an active one.
 */
export function useActivePoint(count: number, onActivate?: (index: number) => void): ActivePoint {
  const [index, setIndex] = useState<number | null>(null);
  const pointer = useRef(false);
  const safe = index !== null && index >= 0 && index < count ? index : null;
  const set = useCallback((next: number | null) => setIndex(next), []);

  const onKeyDown = useCallback(
    (event: KeyboardEvent<HTMLElement>): void => {
      if (count === 0) return;
      const last = count - 1;
      const at = safe ?? last;
      let next: number | null | undefined;
      if (event.key === 'ArrowRight' || event.key === 'ArrowDown') next = Math.min(last, safe === null ? last : at + 1);
      else if (event.key === 'ArrowLeft' || event.key === 'ArrowUp') next = Math.max(0, safe === null ? last : at - 1);
      else if (event.key === 'Home') next = 0;
      else if (event.key === 'End') next = last;
      else if (event.key === 'Escape') next = null;
      else if ((event.key === 'Enter' || event.key === ' ') && onActivate !== undefined) {
        event.preventDefault();
        onActivate(at);
        return;
      }
      if (next === undefined) return;
      event.preventDefault();
      setIndex(next);
    },
    [count, safe, onActivate],
  );

  return {
    index: safe,
    set,
    onKeyDown,
    onFocus: () => {
      // A pointer press focuses too; only a keyboard focus opens the last point on its own.
      if (!pointer.current) setIndex((prev) => prev ?? (count > 0 ? count - 1 : null));
    },
    onBlur: () => setIndex(null),
    onPointerLeave: () => {
      pointer.current = false;
      setIndex(null);
    },
    fromPointer: (event, indexAt) => {
      pointer.current = true;
      const rect = event.currentTarget.getBoundingClientRect();
      const i = indexAt(event.clientX - rect.left);
      setIndex(i >= 0 ? i : null);
    },
    activateFromPointer: (event, indexAt) => {
      if (onActivate === undefined) return;
      const rect = event.currentTarget.getBoundingClientRect();
      const i = indexAt(event.clientX - rect.left);
      if (i >= 0 && i < count) onActivate(i);
    },
  };
}

// ── Axis ticks and labels ──────────────────────────────────────────────────────────────────────────────────────────

/** Width of one mono tick-label character at the micro size, in SVG units. */
const TICK_CHAR_WIDTH = 6.8;
/** The air kept between two neighbouring x labels, in SVG units. */
const X_LABEL_PAD = 14;

/**
 * Every how many categories an x label can be printed so that none touches its neighbour (D854 DS-2): the gap needed is the
 * longest label's own width plus a margin (never less than `minGap`), and `step` is the distance between two categories.
 * A 30-day chart whose labels read "12 de out." therefore prints every second or third day instead of overprinting them all.
 */
export function labelStride(labels: readonly string[], step: number, minGap = 0): number {
  return strideFor(step, Math.max(minGap, longestLabelWidth(labels) + X_LABEL_PAD));
}

/**
 * `labelStride` for a chart that keeps its edge labels inside the plot, the first starting at its point and the last ending at
 * its own (D913). An edge label reaches a whole label width towards its centred neighbour, half a label more than `labelStride`
 * allows: at 30 days the first label printed against the second ("05 de out.08 de out."). The stride stays the same across the
 * axis. When only the first and the last would print, each reaches a whole width towards the other; if that does not fit, the
 * first prints alone.
 */
export function edgeAnchoredLabelStride(labels: readonly string[], step: number, minGap = 0): number {
  const width = longestLabelWidth(labels);
  const stride = strideFor(step, Math.max(minGap, width + width / 2 + X_LABEL_PAD));
  const onlyTheEdges = stride === labels.length - 1;
  return onlyTheEdges && stride * step < width + width + X_LABEL_PAD ? labels.length : stride;
}

function longestLabelWidth(labels: readonly string[]): number {
  return labels.reduce((m, l) => Math.max(m, l.length), 0) * TICK_CHAR_WIDTH;
}

function strideFor(step: number, gap: number): number {
  return Math.max(1, Math.ceil(gap / Math.max(step, 1)));
}

/**
 * Drop the value ticks whose printed label repeats an earlier one (D854 DS-2). On a near-zero scale (a few cents on a money axis)
 * the nice ticks are fractions the formatter rounds to the same text, which printed "R$ 0,01 R$ 0,01 R$ 0,01 R$ 0,00 ...".
 */
export function distinctTicks<T extends { readonly ticks: readonly number[] }>(domain: T, format: (value: number) => string): T {
  const seen = new Set<string>();
  const ticks = domain.ticks.filter((t) => {
    const text = format(t);
    if (seen.has(text)) return false;
    seen.add(text);
    return true;
  });
  return ticks.length === domain.ticks.length ? domain : { ...domain, ticks };
}

/** The tick labels with every repeat blanked (kept in place, so two axes that share gridlines stay aligned). */
export function distinctLabels(texts: readonly string[]): string[] {
  const seen = new Set<string>();
  return texts.map((text) => {
    if (seen.has(text)) return '';
    seen.add(text);
    return text;
  });
}

// ── Pieces ─────────────────────────────────────────────────────────────────────────────────────────────────────────

export interface TooltipRow {
  readonly key: string;
  readonly label?: string;
  readonly value: string;
  /** A series colour swatch (the label also names it: colour is never the only signal). */
  readonly color?: string;
}

/** The tooltip card positioned at (x, y) inside the chart box. Decorative for assistive tech: the live region and the label carry the same text. */
export function ChartTooltip({ x, y, width, heading, rows }: { x: number; y: number; width: number; heading?: string; rows: readonly TooltipRow[] }): ReactElement {
  const align = x < width * 0.18 ? 'start' : x > width * 0.82 ? 'end' : 'center';
  return (
    <div className="vello-chart__tooltip" data-align={align} aria-hidden="true" style={{ left: roundCoord(x), top: roundCoord(Math.max(0, y)) }}>
      {heading !== undefined && <p className="vello-chart__tooltip-heading">{heading}</p>}
      {rows.map((row) => (
        <p key={row.key} className="vello-chart__tooltip-row">
          {row.color !== undefined && <span className="vello-chart__swatch" style={{ background: row.color }} />}
          {row.label !== undefined && <span className="vello-chart__tooltip-label">{row.label}</span>}
          <span className="tnum vello-chart__tooltip-value">{row.value}</span>
        </p>
      ))}
    </div>
  );
}

/** Text for assistive tech only; the active point is announced politely as it moves. */
export function ChartLive({ text }: { text: string }): ReactElement {
  return (
    <span className="vello-chart__sr" aria-live="polite" role="status">
      {text}
    </span>
  );
}

/** The loading placeholder (skeleton shimmer from the effect layer; never a spinner alone). */
export function ChartLoading({ height, label, className }: { height: number; label?: string; className?: string }): ReactElement {
  return (
    <div className={['vello-chart-state', EFFECT_CLASS.skeleton, className].filter(Boolean).join(' ')} role="status" aria-busy="true" aria-label={label} style={{ '--chart-h': `${height}px` } as CSSProperties} />
  );
}

/** The empty placeholder: a quiet box with the caller's localized message, or a dash when none is given. */
export function ChartEmpty({ height, label, className }: { height: number; label?: string; className?: string }): ReactElement {
  return (
    <div className={['vello-chart-state', 'vello-chart-state--empty', className].filter(Boolean).join(' ')} role="img" aria-label={label ?? '–'} style={{ '--chart-h': `${height}px` } as CSSProperties}>
      <span aria-hidden="true">{label ?? '–'}</span>
    </div>
  );
}

/** A series legend: swatch (with the series dash) + name. */
export function ChartLegend({ items }: { items: readonly { key: string; label: string; color: string; dash?: string | undefined; shape?: 'line' | 'bar' | undefined }[] }): ReactElement {
  return (
    <ul className="vello-chart__legend">
      {items.map((item) => (
        <li key={item.key} className="vello-chart__legend-item">
          {item.shape === 'bar' ? (
            <svg className="vello-chart__legend-swatch vello-chart__legend-swatch--bar" viewBox="0 0 16 8" aria-hidden="true">
              <rect x="1" y="0" width="14" height="8" rx="2" fill={item.color} />
            </svg>
          ) : (
            <svg className="vello-chart__legend-swatch" viewBox="0 0 16 4" aria-hidden="true">
              <line x1="0" y1="2" x2="16" y2="2" stroke={item.color} strokeWidth="2" strokeLinecap="round" strokeDasharray={item.dash} />
            </svg>
          )}
          <span>{item.label}</span>
        </li>
      ))}
    </ul>
  );
}

/** Join the parts of an accessible label (a title and one summary per series) into one sentence-like string. */
export function joinSummary(parts: readonly (string | undefined)[]): string {
  return parts.filter((p): p is string => typeof p === 'string' && p !== '').join('. ');
}

/** Join class names, skipping empty ones. */
export function cx(...names: (string | false | null | undefined)[]): string {
  return names.filter((n): n is string => typeof n === 'string' && n !== '').join(' ');
}

export type { ReactNode };
